/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import Button, {
  BUTTON_COLORS,
  BUTTON_SIZES,
  BUTTON_VARIANTS
} from "@/components/Button/Button";
import Tooltip from "@/components/Tooltip/Tooltip";
import { useDashboardStore } from "@/stores/useDashboardStore";
import Typography from "@mui/material/Typography";
import { useResizeObserver } from "@usefy/use-resize-observer";

import { OpenInNewIcon } from "@dotnet/telematics-design-system";

import Badge from "../../../../components/Badge/Badge";
import { useDashboardContext } from "../../../../context/dashoard/DashboardProvider";
import { useDateTime, useMediaQueries } from "../../../../shared";
import RectangleLoader from "../../../../shared/components/loaders/RectangleLoader";
import CardWrapper from "../../CardWrapper/CardWrapper";
import { creditsObjectType, CreditsStatuses } from "../types";
import CreditsHorizontalBarChart from "./components/CreditsHorizontalChart/CreditsHorizontalChart";
import CreditsLegendCards from "./components/CreditsLegendCards/CreditsLegendCards";
import HealthSection from "./components/HealthSection/HealthSection";
import HistoryDialog from "./components/HistoryDialog/HistoryDialog";
import ViewHistoryButton from "./components/ViewHistoryButton/ViewHistoryButton";
import { DEMO_CREDITS_COUNT } from "./config";
import useCredits from "./hooks/useCredits";
import HowCreditsWorkDialog from "./HowCreditsWorkDialog/HowCreditsWorkDialog";

const Credits = () => {
  const { setDashboardWidgets } = useDashboardContext();
  const { isLoading: isCreditsLoading } = useCredits();
  const { toSm, toLg } = useMediaQueries();
  const { ref, height = 1 } = useResizeObserver();

  const { t } = useTranslation();
  const { formatDateToEnString } = useDateTime();

  const {
    isDashboardLoading,
    credits: creditsData,
    creditsError,
    setNewsHeight
  } = useDashboardStore();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [howCreditsWorkOpen, setHowCreditsWorkOpen] = useState(false);

  const handleCloseHistoryDialog = () => {
    setHistoryOpen(false);
  };

  const handleOpenHistoryDialog = () => {
    setHistoryOpen(true);
  };

  const handleSetHowCreditsWorkDialog = (isOpen: boolean) => {
    setHowCreditsWorkOpen(isOpen);
  };

  const isLoading = isDashboardLoading || isCreditsLoading;

  useEffect(() => {
    setDashboardWidgets(prev => {
      return { ...prev, showCredits: true };
    });
  }, [setDashboardWidgets]);

  useEffect(() => {
    setNewsHeight(height);
  }, [height]);

  const creditsObjectArray: creditsObjectType[] | null = creditsData && [
    {
      name: CreditsStatuses.Balance,
      count: creditsData.balance
    },
    {
      name: CreditsStatuses.Reserved,
      count: creditsData.reserved
    },
    {
      name: CreditsStatuses.Available,
      count: creditsData.available
    },
    ...(creditsData.overDraftAmount
      ? [
          {
            name: CreditsStatuses.OverDraft,
            count: creditsData.overDraftAmount
          }
        ]
      : [])
  ];

  const titleInlineAction = creditsData?.expiresAt ? (
    <div
      css={css({
        display: "flex",
        alignItems: "center",
        gap: "8px"
      })}
    >
      <Typography variant="body3Medium">
        {t("Dashboard##expires")}:{" "}
        {formatDateToEnString(creditsData?.expiresAt, true)}
      </Typography>
      {!toSm && (
        <ViewHistoryButton
          toSm={toSm}
          handleOpenHistoryDialog={handleOpenHistoryDialog}
        />
      )}
    </div>
  ) : undefined;

  const isInactiveCredits = creditsData?.available === DEMO_CREDITS_COUNT;
  const isExpired = creditsData?.isDemoExpired;
  const isUsedUp = creditsData?.isDemoUsedUp;
  const isDemoCredits = creditsData?.isDemo && isInactiveCredits;
  const expiredOrUsedUp = isExpired || isUsedUp;

  const isExpiredText = t(
    "Dashboard##demo credits##demo credits expired tooltip"
  );

  const isUsedUpText = t(
    "Dashboard##demo credits##demo credits used up tooltip"
  );

  const creditsContent = () => {
    if (isLoading) {
      return (
        <RectangleLoader
          width="100%"
          height="100%"
          customStyle={{
            minHeight: toLg ? "386px" : "206px"
          }}
          testId="credits-card-loader"
        />
      );
    }

    if (!creditsData) {
      return (
        <div
          css={css({
            height: "202px"
          })}
        />
      );
    }

    return (
      <>
        {isDemoCredits && (
          <BannerMessage
            data-testid="demo-credits-info-banner"
            status={BANNER_MESSAGE_STATUSES.Info}
            title={t("Dashboard##demo credits##demo info tooltip")}
            externalCustomStyle={{ marginBottom: "16px" }}
          />
        )}

        {expiredOrUsedUp && (
          <BannerMessage
            data-testid="demo-credits-expired-warning-banner"
            status={BANNER_MESSAGE_STATUSES.Warning}
            title={isUsedUp ? isUsedUpText : isExpiredText}
            externalCustomStyle={{ marginBottom: "16px" }}
          />
        )}

        <HealthSection creditsData={creditsData} />

        <CreditsHorizontalBarChart
          creditsObjectArray={creditsObjectArray}
          totalWithOverdraft={creditsData.totalWithOverdraft}
          healthStatus={creditsData.health}
        />

        <CreditsLegendCards creditsObjectArray={creditsObjectArray} />

        {toSm && (
          <ViewHistoryButton
            toSm={toSm}
            handleOpenHistoryDialog={handleOpenHistoryDialog}
          />
        )}
      </>
    );
  };

  return (
    <>
      <CardWrapper
        cardTitle={t("Dashboard##credits")}
        titleExtention={
          <>
            <Tooltip
              title={t("Dashboard##how credits work##button tooltip")}
              placement="top"
              size="medium"
              hasArrow
            >
              <Button
                color={BUTTON_COLORS.White}
                variant={BUTTON_VARIANTS.IconOnly}
                size={BUTTON_SIZES.Tiny}
                icon={
                  <OpenInNewIcon
                    css={css({
                      width: "16px",
                      height: "16px"
                    })}
                  />
                }
                onClick={() => handleSetHowCreditsWorkDialog(true)}
              />
            </Tooltip>

            {creditsData?.isDemo && (
              <Badge
                color="light"
                customStyle={css({ marginLeft: "8px", cursor: "unset" })}
                tooltipText={t("Dashboard##demo credits##demo info tooltip")}
                tooltipSize="medium"
              >
                {t("Dashboard##demo credits##demo")}
              </Badge>
            )}
          </>
        }
        isCreditsCard
        isLoading={isLoading}
        titleInlineAction={titleInlineAction}
        isError={creditsError}
      >
        <div ref={ref}>{creditsContent()}</div>
      </CardWrapper>

      {creditsData && (
        <HistoryDialog
          open={historyOpen}
          onClose={handleCloseHistoryDialog}
          creditsData={creditsData}
        />
      )}

      {howCreditsWorkOpen && (
        <HowCreditsWorkDialog
          open={howCreditsWorkOpen}
          onClose={() => handleSetHowCreditsWorkDialog(false)}
        />
      )}
    </>
  );
};

export default Credits;
